import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCart } from '@/contexts/CartContext';

const CartReplacementDialog = () => {
  const { pendingAddition, confirmAddition, cancelAddition } = useCart();

  if (!pendingAddition) return null;

  const { template, productsToRemove, replacementType } = pendingAddition;

  const isUpgrade = replacementType === 'upgrade';
  const isDowngrade = replacementType === 'downgrade';
  const isTierSwitch = replacementType === 'tier-switch';

  const currentTier = productsToRemove[0]?.template;
  const newTier = template;
  const isUpgradingTier = isTierSwitch && newTier.price > (currentTier?.price || 0);

  const getTitle = () => {
    if (isTierSwitch) {
      return isUpgradingTier ? 'Upgrade Your Package?' : 'Switch Package?';
    }
    return isUpgrade ? 'Upgrade to Complete Package?' : 'Downgrade to Individual Template?';
  };

  const getCancelText = () => {
    if (isTierSwitch) {
      return `Keep ${currentTier?.name}`;
    }
    return isUpgrade ? 'Keep Individual Items' : 'Keep Package';
  };

  const getConfirmText = () => {
    if (isTierSwitch) {
      return isUpgradingTier ? 'Yes, Upgrade!' : 'Yes, Switch';
    }
    return isUpgrade ? 'Yes, Upgrade!' : 'Yes, Switch';
  };

  return (
    <AlertDialog open={!!pendingAddition} onOpenChange={(open) => !open && cancelAddition()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{getTitle()}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {isTierSwitch ? (
                <>
                  <p>
                    You currently have <strong>{currentTier?.name}</strong> in your cart.
                    Would you like to switch to <strong>{newTier.name}</strong>?
                  </p>
                  
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Current selection:</span>
                      <span className="font-medium">{currentTier?.name} - ${currentTier?.price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">New selection:</span>
                      <span className="font-semibold text-primary">{newTier.name} - ${newTier.price.toFixed(2)}</span>
                    </div>
                    {isUpgradingTier && (
                      <div className="border-t border-border pt-2 flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Difference:</span>
                        <span className="font-medium text-primary">+${(newTier.price - (currentTier?.price || 0)).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    {isUpgradingTier 
                      ? 'The higher tier includes more features and better value!'
                      : 'You can only have one package review tier at a time.'}
                  </p>
                </>
              ) : isUpgrade ? (
                <>
                  <p>
                    Great choice! Adding <strong>{template.name}</strong> includes all individual templates. 
                    The following item(s) will be removed since they're already included:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    {productsToRemove.map((item) => (
                      <li key={item.template.id} className="text-sm">
                        {item.template.name} - ${item.template.price.toFixed(2)}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground">
                    This is a better deal - you get everything in one package!
                  </p>
                </>
              ) : (
                <>
                  <p>
                    You currently have the complete DIY kit which includes all templates. 
                    Adding <strong>{template.name}</strong> as an individual purchase will replace your bundle.
                  </p>
                  
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Current bundle price:</span>
                      <span className="font-semibold text-primary">${productsToRemove[0]?.template.price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Individual template price:</span>
                      <span className="font-medium">${template.price.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-border pt-2 flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">You'd lose access to:</span>
                      <span className="font-medium text-destructive">All other templates</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    Keep the bundle for the best value! You get all templates for just ${productsToRemove[0]?.template.price.toFixed(2)}.
                  </p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={cancelAddition}>
            {getCancelText()}
          </AlertDialogCancel>
          <AlertDialogAction onClick={confirmAddition}>
            {getConfirmText()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CartReplacementDialog;
